import { BodyText, Caption } from '@farol-ds/react'
import classNames from 'classnames'
import Link from 'next/link'

import styles from './auth-footer.module.scss'

type AuthFooterTextComponent = 'caption' | 'body'
export type AuthFooterLinkAppearance = 'default' | 'brand' | 'brandMuted'

export interface AuthFooterLinkLabels {
  terms: string
  privacy: string
}

interface AuthFooterProps {
  showAgreementText?: boolean
  className?: string
  textPrefix?: string
  textComponent?: AuthFooterTextComponent
  linkAppearance?: AuthFooterLinkAppearance
  linkUnderline?: boolean
  linkLabels?: AuthFooterLinkLabels
  trailingPeriod?: boolean
}

const TERMS_URL = 'https://www.jusbrasil.com.br/termos-de-uso'
const PRIVACY_URL = 'https://www.jusbrasil.com.br/politica-de-privacidade'
const DEFAULT_AGREEMENT_TEXT_PREFIX = 'Ao continuar eu concordo com os '
const DEFAULT_LINK_LABELS: AuthFooterLinkLabels = {
  terms: 'Termos de Uso',
  privacy: 'Política de Privacidade',
}

// Hand-copies @farol-ds/react Link's `type="primary"`/`type="tertiary"`
// palettes (never imports Farol's own Link — that would change the default
// anchor's markup/behavior for every existing caller, most of which pass no
// appearance at all). `brandMuted` is variant C's Finalize-step gray, which
// no Farol Link `type` resolves exactly — see the double-class specificity
// note on `.footerLinkBrandMuted` below.
const LINK_CLASS_BY_APPEARANCE: Record<AuthFooterLinkAppearance, string | undefined> = {
  default: undefined,
  brand: styles.footerLinkBrand,
  brandMuted: styles.footerLinkBrandMuted,
}

export function AuthFooter({
  showAgreementText = false,
  className,
  textPrefix,
  textComponent = 'caption',
  linkAppearance = 'default',
  linkUnderline = false,
  linkLabels = DEFAULT_LINK_LABELS,
  trailingPeriod = false,
}: AuthFooterProps) {
  const resolvedPrefix =
    textPrefix ?? (showAgreementText ? DEFAULT_AGREEMENT_TEXT_PREFIX : undefined)
  const linkProps = {
    target: '_blank' as const,
    rel: 'noopener noreferrer' as const,
    className: classNames(styles.footerLink, LINK_CLASS_BY_APPEARANCE[linkAppearance], {
      [styles.footerLinkUnderline]: linkUnderline,
    }),
  }
  const TextComponent = textComponent === 'body' ? BodyText : Caption

  return (
    <footer className={classNames(styles.footer, className)}>
      <div className={styles.footerInner}>
        <TextComponent
          className={classNames(styles.footerText, {
            // Entity-notice's body-sized usage keeps QA2-8j's own measured
            // typography (3xs/md/low-1) instead of BodyText's own default
            // (2xs/lg/low-2) — the only caller of textComponent="body" today.
            [styles.footerTextBody]: textComponent === 'body',
          })}
          asChild
        >
          <span>
            {resolvedPrefix}
            <Link href={TERMS_URL} {...linkProps}>
              {linkLabels.terms}
            </Link>
            {' e a '}
            <Link href={PRIVACY_URL} {...linkProps}>
              {linkLabels.privacy}
            </Link>
            {trailingPeriod && '.'}
          </span>
        </TextComponent>
      </div>
    </footer>
  )
}
